/**
 * Phone Number Group Store for RepuCall
 *
 * This store manages phone number groups using Zustand and Supabase.
 * Includes comprehensive logging, error handling, and CRUD operations.
 */

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { supabase } from '@/lib/supabase';
import { Logger, LogCategory, logDataOperation, logStateChange } from '@/utils/logger';
import { PHONE_NUMBER_GROUP_COLORS, REPUTATION_SOURCES } from '@/constants';
import type { PhoneNumberGroup, ReputationSource } from '@/types';

// ============================================================================
// Types
// ============================================================================

interface PhoneNumberGroupState {
  // Data
  groups: PhoneNumberGroup[];

  // UI State
  isLoading: boolean;
  isSaving: boolean;
  error: string | null;

  // Actions
  fetchGroups: () => Promise<void>;
  createGroup: (group: Omit<PhoneNumberGroup, 'id' | 'createdAt' | 'updatedAt' | 'phoneCount'>) => Promise<PhoneNumberGroup>;
  updateGroup: (id: string, updates: Partial<PhoneNumberGroup>) => Promise<void>;
  deleteGroup: (id: string) => Promise<void>;
  getGroupById: (id: string) => PhoneNumberGroup | undefined;
  ensureDefaultGroup: (companyId: string) => Promise<PhoneNumberGroup>;

  // Utility Actions
  clearError: () => void;
}

// ============================================================================
// Store Implementation
// ============================================================================

export const usePhoneNumberGroupStore = create<PhoneNumberGroupState>()(
  devtools(
    (set, get) => ({
      // ========================================================================
      // Initial State
      // ========================================================================
      groups: [],
      isLoading: false,
      isSaving: false,
      error: null,

      // ========================================================================
      // Fetch Groups
      // ========================================================================
      fetchGroups: async () => {
        Logger.info(LogCategory.DATA, 'Fetching phone number groups from Supabase', 'fetchGroups');
        set({ isLoading: true, error: null });

        try {
          // Get current session
          const { data: { session } } = await supabase.auth.getSession();
          if (!session) {
            throw new Error('Not authenticated');
          }

          // Get user's company_id
          const { data: user, error: userError } = await supabase
            .from('users')
            .select('company_id')
            .eq('id', session.user.id)
            .single();

          if (userError) throw userError;
          if (!user) throw new Error('User not found');

          // Fetch groups from Supabase
          const { data: dbGroups, error: groupsError } = await supabase
            .from('phone_number_groups')
            .select('*')
            .eq('company_id', (user as any).company_id)
            .order('created_at');

          if (groupsError) throw groupsError;

          // Fetch phone counts per group
          const { data: phoneCounts, error: countError } = await supabase
            .from('phone_numbers')
            .select('phone_number_group_id')
            .eq('company_id', (user as any).company_id)
            .eq('is_archived', false);

          if (countError) {
            Logger.warn(LogCategory.DATA, 'Failed to fetch phone counts', 'fetchGroups', undefined, countError);
          }

          // Build a map of group_id -> count
          const countMap = new Map<string, number>();
          (phoneCounts || []).forEach((p: any) => {
            if (p.phone_number_group_id) {
              countMap.set(p.phone_number_group_id, (countMap.get(p.phone_number_group_id) || 0) + 1);
            }
          });

          // Transform database format to app format
          const groups: PhoneNumberGroup[] = (dbGroups || []).map((dbGroup: any) => ({
            id: dbGroup.id,
            name: dbGroup.name,
            description: dbGroup.description,
            color: dbGroup.color,
            companyId: dbGroup.company_id,
            phoneCount: countMap.get(dbGroup.id) || 0,
            enabledReputationSources: dbGroup.enabled_reputation_sources || [],
            createdAt: dbGroup.created_at,
            updatedAt: dbGroup.updated_at,
          }));

          Logger.info(LogCategory.DATA, `Fetched ${groups.length} groups from Supabase`, 'fetchGroups');

          logStateChange('phoneNumberGroupStore', 'setGroups', [], groups);

          set({
            groups,
            isLoading: false,
            error: null,
          });

          logDataOperation('fetch', 'phoneNumberGroups', groups.length);
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Failed to fetch phone number groups';
          Logger.error(LogCategory.DATA, errorMessage, 'fetchGroups', undefined, error);

          set({
            isLoading: false,
            error: errorMessage,
          });

          logDataOperation('fetch', 'phoneNumberGroups', undefined, { errorMessage }, error);
        }
      },

      // ========================================================================
      // Create Group
      // ========================================================================
      createGroup: async (groupData) => {
        Logger.info(LogCategory.DATA, 'Creating phone number group', 'createGroup', {
          name: groupData.name,
        });
        set({ isSaving: true, error: null });

        try {
          const currentGroups = get().groups;

          // Check for duplicate names
          if (currentGroups.some(g => g.name.toLowerCase() === groupData.name.toLowerCase())) {
            throw new Error('A group with this name already exists');
          }

          // Insert into Supabase
          const { data: dbGroup, error: insertError } = await supabase
            .from('phone_number_groups')
            .insert({
              name: groupData.name,
              description: groupData.description || null,
              color: groupData.color || PHONE_NUMBER_GROUP_COLORS[currentGroups.length % PHONE_NUMBER_GROUP_COLORS.length].value,
              company_id: groupData.companyId,
              enabled_reputation_sources: groupData.enabledReputationSources,
            } as any)
            .select()
            .single();

          if (insertError) throw insertError;
          if (!dbGroup) throw new Error('Failed to create group');

          // Transform to app format
          const newGroup: PhoneNumberGroup = {
            id: (dbGroup as any).id,
            name: (dbGroup as any).name,
            description: (dbGroup as any).description,
            color: (dbGroup as any).color,
            companyId: (dbGroup as any).company_id,
            phoneCount: 0,
            enabledReputationSources: (dbGroup as any).enabled_reputation_sources || [],
            createdAt: (dbGroup as any).created_at,
            updatedAt: (dbGroup as any).updated_at,
          };

          Logger.info(LogCategory.DATA, 'Phone number group created', 'createGroup', {
            groupId: newGroup.id,
            name: newGroup.name,
          });

          const updatedGroups = [...currentGroups, newGroup];

          set({
            groups: updatedGroups,
            isSaving: false,
            error: null,
          });

          logDataOperation('create', 'phoneNumberGroup', 1, { id: newGroup.id });

          return newGroup;
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Failed to create phone number group';
          Logger.error(LogCategory.DATA, errorMessage, 'createGroup', undefined, error);

          set({
            isSaving: false,
            error: errorMessage,
          });

          logDataOperation('create', 'phoneNumberGroup', undefined, { errorMessage }, error);
          throw error;
        }
      },

      // ========================================================================
      // Update Group
      // ========================================================================
      updateGroup: async (id, updates) => {
        Logger.info(LogCategory.DATA, 'Updating phone number group', 'updateGroup', {
          groupId: id,
          updates: Object.keys(updates),
        });
        set({ isSaving: true, error: null });

        try {
          const currentGroups = get().groups;
          const groupIndex = currentGroups.findIndex(g => g.id === id);

          if (groupIndex === -1) {
            throw new Error('Phone number group not found');
          }

          // Check for duplicate names if name is being updated
          if (updates.name) {
            const duplicateExists = currentGroups.some(
              g => g.id !== id && g.name.toLowerCase() === updates.name!.toLowerCase()
            );
            if (duplicateExists) {
              throw new Error('A group with this name already exists');
            }
          }

          // Prepare database updates
          const dbUpdates: any = {};
          if (updates.name !== undefined) dbUpdates.name = updates.name;
          if (updates.description !== undefined) dbUpdates.description = updates.description;
          if (updates.color !== undefined) dbUpdates.color = updates.color;
          if (updates.enabledReputationSources !== undefined) {
            dbUpdates.enabled_reputation_sources = updates.enabledReputationSources;
          }

          // Update in Supabase
          const { data: dbGroup, error: updateError } = await (supabase
            .from('phone_number_groups') as any)
            .update(dbUpdates)
            .eq('id', id)
            .select()
            .single();

          if (updateError) throw updateError;
          if (!dbGroup) throw new Error('Failed to update group');

          // Transform to app format
          const updatedGroup: PhoneNumberGroup = {
            ...currentGroups[groupIndex],
            name: (dbGroup as any).name,
            description: (dbGroup as any).description,
            color: (dbGroup as any).color,
            enabledReputationSources: (dbGroup as any).enabled_reputation_sources || [],
            updatedAt: (dbGroup as any).updated_at,
          };

          const updatedGroups = [...currentGroups];
          updatedGroups[groupIndex] = updatedGroup;

          Logger.info(LogCategory.DATA, 'Phone number group updated', 'updateGroup', {
            groupId: id,
            updatedFields: Object.keys(updates),
          });

          set({
            groups: updatedGroups,
            isSaving: false,
            error: null,
          });

          logDataOperation('update', 'phoneNumberGroup', 1, { id });
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Failed to update phone number group';
          Logger.error(LogCategory.DATA, errorMessage, 'updateGroup', undefined, error);

          set({
            isSaving: false,
            error: errorMessage,
          });

          logDataOperation('update', 'phoneNumberGroup', undefined, { errorMessage }, error);
          throw error;
        }
      },

      // ========================================================================
      // Delete Group
      // ========================================================================
      deleteGroup: async (id) => {
        Logger.info(LogCategory.DATA, 'Deleting phone number group', 'deleteGroup', {
          groupId: id,
        });
        set({ isSaving: true, error: null });

        try {
          const currentGroups = get().groups;
          const groupToDelete = currentGroups.find(g => g.id === id);

          if (!groupToDelete) {
            throw new Error('Phone number group not found');
          }

          // Prevent deleting the Default group
          if (groupToDelete.name === 'Default') {
            throw new Error('Cannot delete the Default group');
          }

          // Find the Default group to reassign phones to
          const defaultGroup = currentGroups.find(g => g.name === 'Default');
          if (!defaultGroup) {
            throw new Error('Default group not found. Cannot reassign phone numbers.');
          }

          // Reassign phones from this group to Default before deleting
          const { error: reassignError } = await (supabase
            .from('phone_numbers') as any)
            .update({ phone_number_group_id: defaultGroup.id })
            .eq('phone_number_group_id', id);

          if (reassignError) throw reassignError;

          // Delete from Supabase
          const { error: deleteError } = await supabase
            .from('phone_number_groups')
            .delete()
            .eq('id', id);

          if (deleteError) throw deleteError;

          const updatedGroups = currentGroups.filter(g => g.id !== id);

          Logger.info(LogCategory.DATA, 'Phone number group deleted', 'deleteGroup', {
            groupId: id,
            name: groupToDelete.name,
          });

          set({
            groups: updatedGroups,
            isSaving: false,
            error: null,
          });

          logDataOperation('delete', 'phoneNumberGroup', 1, { id });
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Failed to delete phone number group';
          Logger.error(LogCategory.DATA, errorMessage, 'deleteGroup', undefined, error);

          set({
            isSaving: false,
            error: errorMessage,
          });

          logDataOperation('delete', 'phoneNumberGroup', undefined, { errorMessage }, error);
          throw error;
        }
      },

      // ========================================================================
      // Get Group By ID
      // ========================================================================
      getGroupById: (id) => {
        return get().groups.find(g => g.id === id);
      },

      // ========================================================================
      // Ensure Default Group Exists
      // ========================================================================
      ensureDefaultGroup: async (companyId) => {
        Logger.info(LogCategory.DATA, 'Ensuring default group exists', 'ensureDefaultGroup');

        const currentGroups = get().groups;

        // Check if a default group already exists
        let defaultGroup = currentGroups.find(g => g.name === 'Default');

        if (!defaultGroup) {
          Logger.info(LogCategory.DATA, 'Creating default group', 'ensureDefaultGroup');

          // Create default group with all sources enabled
          const defaultSources: ReputationSource[] = [
            'Nomorobo',
            'RoboKiller',
            'IceHook',
            'TrueSpam',
            'YouMail',
            'Sensor',
            'Verizon',
            'FTC',
          ];

          defaultGroup = await get().createGroup({
            name: 'Default',
            description: 'Default group for all phone numbers',
            color: PHONE_NUMBER_GROUP_COLORS[0].value,
            companyId,
            enabledReputationSources: defaultSources,
          });
        }

        return defaultGroup;
      },

      // ========================================================================
      // Clear Error
      // ========================================================================
      clearError: () => {
        Logger.debug(LogCategory.UI, 'Clearing error state', 'clearError');
        set({ error: null });
      },
    }),
    { name: 'phone-number-group-store' }
  )
);

// ============================================================================
// Selector Hooks
// ============================================================================

export const usePhoneNumberGroups = () => usePhoneNumberGroupStore((state) => state.groups);
export const usePhoneNumberGroupsLoading = () => usePhoneNumberGroupStore((state) => state.isLoading);
export const usePhoneNumberGroupsSaving = () => usePhoneNumberGroupStore((state) => state.isSaving);
export const usePhoneNumberGroupsError = () => usePhoneNumberGroupStore((state) => state.error);
